import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { bookingAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Receipt, CheckCircle, XCircle, Eye, Loader2, Building2,
  CreditCard, FileText, Download, Clock, Search, Hash
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentVerification = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifiedBookings, setVerifiedBookings] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showProofDialog, setShowProofDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [tab, setTab] = useState('pending');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, verifiedRes, receiptsRes] = await Promise.all([
        bookingAPI.getPendingVerification(),
        bookingAPI.getBookings({ status: 'payment_verified' }),
        bookingAPI.getReceipts(),
      ]);
      setBookings(pendingRes.data || []);
      setVerifiedBookings(verifiedRes.data || []);
      setReceipts(receiptsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (approved) => {
    if (!selectedBooking) return;
    setProcessing(true);
    try {
      await bookingAPI.verifyPayment(selectedBooking.id, approved, verifyNotes);
      toast.success(approved ? 'Payment verified successfully' : 'Payment rejected');
      setShowVerifyDialog(false);
      setVerifyNotes('');
      setSelectedBooking(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process verification');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadReceipt = (bookingId) => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/bookings/${bookingId}/receipt`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Failed');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `receipt_${bookingId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Failed to download receipt'));
  };

  // Filter logic per tab
  const getFilteredList = () => {
    const q = searchQuery.toLowerCase();
    if (tab === 'pending') {
      return bookings.filter(b => !q ||
        b.buyer_name?.toLowerCase().includes(q) ||
        b.property?.name?.toLowerCase().includes(q) ||
        b.bank_reference?.toLowerCase().includes(q)
      );
    }
    if (tab === 'verified') {
      return verifiedBookings.filter(b => !q ||
        b.buyer_name?.toLowerCase().includes(q) ||
        b.property?.name?.toLowerCase().includes(q) ||
        b.bank_reference?.toLowerCase().includes(q)
      );
    }
    // receipts tab
    return receipts.filter(r => !q ||
      r.serial_no?.toLowerCase().includes(q) ||
      r.buyer_name?.toLowerCase().includes(q) ||
      r.property_name?.toLowerCase().includes(q) ||
      r.bank_reference?.toLowerCase().includes(q)
    );
  };

  const filteredList = getFilteredList();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-verification-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payment Verification</h1>
          <p className="text-slate-500 text-sm mt-1">Review and verify booking fee payments</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
            <Clock className="w-4 h-4 mr-1 inline" />
            {bookings.length} Pending
          </Badge>
          <Badge className="bg-emerald-100 text-emerald-700 text-sm px-3 py-1">
            <CheckCircle className="w-4 h-4 mr-1 inline" />
            {verifiedBookings.length} Verified
          </Badge>
          <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">
            <Receipt className="w-4 h-4 mr-1 inline" />
            {receipts.length} Receipts
          </Badge>
        </div>
      </div>

      {/* Tabs & Search */}
      <div className="flex items-center gap-4">
        <div className="flex bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setTab('pending')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-pending"
          >
            Pending ({bookings.length})
          </button>
          <button
            onClick={() => setTab('verified')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'verified' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-verified"
          >
            Verified ({verifiedBookings.length})
          </button>
          <button
            onClick={() => setTab('receipts')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'receipts' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-receipts"
          >
            Receipts ({receipts.length})
          </button>
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={tab === 'receipts' ? 'Search by serial no, name...' : 'Search by name, property, ref...'}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Content per tab */}
      {filteredList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">
              {tab === 'pending' ? 'No pending payment verifications' :
               tab === 'verified' ? 'No verified payments yet' :
               'No receipts issued yet'}
            </p>
          </CardContent>
        </Card>
      ) : tab === 'receipts' ? (
        /* ===== RECEIPTS TAB ===== */
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Serial No</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Buyer</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Property</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Unit</th>
                <th className="text-right py-3 px-4 font-semibold text-slate-700">Amount (RM)</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Method</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Bank Ref</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Verified By</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-700">Date</th>
                <th className="text-center py-3 px-4 font-semibold text-slate-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.map((receipt) => (
                <tr key={receipt.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`receipt-row-${receipt.serial_no}`}>
                  <td className="py-3 px-4">
                    <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">
                      <Hash className="w-3 h-3 mr-0.5 inline" />{receipt.serial_no}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-900">{receipt.buyer_name}</td>
                  <td className="py-3 px-4 text-slate-600">{receipt.property_name}</td>
                  <td className="py-3 px-4 text-slate-600">{receipt.unit_number}</td>
                  <td className="py-3 px-4 text-right font-mono text-slate-900">{(receipt.booking_fee_amount || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-slate-600 capitalize">{(receipt.payment_method || '-').replace('_', ' ')}</td>
                  <td className="py-3 px-4 text-slate-500 text-xs">{receipt.bank_reference}</td>
                  <td className="py-3 px-4 text-slate-600">{receipt.verified_by_name}</td>
                  <td className="py-3 px-4 text-slate-500 text-xs">{receipt.verified_at ? new Date(receipt.verified_at).toLocaleDateString() : '-'}</td>
                  <td className="py-3 px-4 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadReceipt(receipt.booking_id)}
                      data-testid={`download-receipt-${receipt.serial_no}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ===== PENDING / VERIFIED TABS ===== */
        <div className="space-y-3">
          {filteredList.map((booking) => (
            <Card key={booking.id} className="border border-slate-200 hover:shadow-md transition-shadow" data-testid={`booking-card-${booking.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-slate-900">{booking.buyer_name || booking.form_data?.buyer_name || 'N/A'}</h3>
                      <Badge className={`text-xs ${booking.status === 'payment_verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {booking.status === 'payment_verified' ? 'Verified' : 'Pending Verification'}
                      </Badge>
                      {booking.receipt_serial_no && (
                        <Badge className="bg-blue-100 text-blue-700 font-mono text-xs">
                          <Hash className="w-3 h-3 mr-0.5 inline" />{booking.receipt_serial_no}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-600">{booking.property?.name || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-600 font-mono">RM {(booking.booking_fee_amount || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-600 capitalize">{(booking.payment_method || '-').replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                        Ref: {booking.bank_reference || '-'}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">
                      Agent: {booking.agent?.full_name || 'N/A'} | Created: {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '-'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {booking.payment_proof && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedBooking(booking); setShowProofDialog(true); }}
                        data-testid={`view-proof-${booking.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />Proof
                      </Button>
                    )}
                    {tab === 'pending' && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => { setSelectedBooking(booking); setShowVerifyDialog(true); }}
                        data-testid={`verify-btn-${booking.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />Verify
                      </Button>
                    )}
                    {tab === 'verified' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadReceipt(booking.id)}
                        data-testid={`download-receipt-${booking.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" />Receipt
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Payment Proof Dialog */}
      <Dialog open={showProofDialog} onOpenChange={setShowProofDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-emerald-600" />
              Payment Proof - {selectedBooking?.buyer_name || 'N/A'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-500">Amount:</span> <span className="font-mono font-semibold ml-1">RM {(selectedBooking?.booking_fee_amount || 0).toLocaleString()}</span></div>
              <div><span className="text-slate-500">Method:</span> <span className="capitalize ml-1">{(selectedBooking?.payment_method || '-').replace('_', ' ')}</span></div>
              <div><span className="text-slate-500">Bank Ref:</span> <span className="ml-1">{selectedBooking?.bank_reference || '-'}</span></div>
              <div><span className="text-slate-500">Property:</span> <span className="ml-1">{selectedBooking?.property?.name || '-'}</span></div>
            </div>
            <div className="border-t border-slate-200" />
            {selectedBooking?.payment_proof && (
              <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto bg-slate-50">
                {selectedBooking.payment_proof.startsWith('data:image') ? (
                  <img src={selectedBooking.payment_proof} alt="Payment proof" className="w-full" data-testid="payment-proof-image" />
                ) : (
                  <div className="p-8 text-center text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-2" />
                    <p>PDF payment proof uploaded</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify/Reject Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Verify Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
              <p><span className="text-slate-500">Buyer:</span> <span className="font-medium">{selectedBooking?.buyer_name || selectedBooking?.form_data?.buyer_name || 'N/A'}</span></p>
              <p><span className="text-slate-500">Amount:</span> <span className="font-mono font-semibold">RM {(selectedBooking?.booking_fee_amount || 0).toLocaleString()}</span></p>
              <p><span className="text-slate-500">Bank Ref:</span> {selectedBooking?.bank_reference || '-'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Verification Notes (optional)</label>
              <Textarea
                value={verifyNotes}
                onChange={(e) => setVerifyNotes(e.target.value)}
                placeholder="Add any notes about this verification..."
                className="mt-1"
                data-testid="verify-notes"
              />
            </div>
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleVerify(true)}
                disabled={processing}
                data-testid="confirm-verify-btn"
              >
                {processing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Approve Payment
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => handleVerify(false)}
                disabled={processing}
                data-testid="reject-verify-btn"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentVerification;
